// [S-475] OAuth consent screen for the claude.ai (remote MCP) connector.
//
// Flow: claude.ai → MCP server GET /authorize (validates client/redirect/PKCE)
// → 302 to this page (MCP_AUTHORIZE_WEB_URL) carrying the OAuth params as query
// string. This screen confirms the user is signed into ContractStudio, shows a
// consent card, and on "Allow" POSTs a real <form> to ${mcp_base}/authorize/
// complete with the user's Identity Platform ID token. The MCP server verifies
// the token, mints the organization-pinned code (S-472), and 302s the browser back to
// claude.ai with code+state.
//
// A native form POST (not fetch) is deliberate: it's a top-level navigation, so
// the server's 302 redirects the browser to claude.ai with no CORS preflight
// and no opaque-redirect handling.
import { useMemo, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Button } from "../components";
import { stashOAuthReturn } from "../auth/oauthReturn";

// The MCP server's public base (where /authorize/complete lives) arrives as the
// `mcp_base` query param — i.e. attacker-influenceable. We POST the user's ID
// token there, so the origin MUST be vetted before submitting. Allow the
// configured MCP base, any per-tenant `csai-mcp-*` Cloud Run host, or localhost
// (dev). Anything else is rejected and the token is never sent.
function isTrustedMcpBase(raw: string | null): raw is string {
  if (raw === null || raw.length === 0) return false;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return true;
  if (u.protocol !== "https:") return false;
  const configured = import.meta.env.VITE_MCP_BASE;
  if (configured !== undefined && configured.length > 0) {
    try {
      if (new URL(configured).origin === u.origin) return true;
    } catch {
      /* fall through to the host-pattern check */
    }
  }
  return u.host.startsWith("csai-mcp-") && u.host.endsWith(".run.app");
}

export function McpConsent(): JSX.Element {
  const { state, getFreshJwt } = useAuth();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declined, setDeclined] = useState(false);

  // Only meaningful for an authenticated user; App.tsx gates this route, but
  // narrow here so TypeScript knows `session` exists below.
  const session = state.status === "authenticated" ? state.session : null;

  const req = useMemo(() => {
    const clientId = params.get("client_id");
    const redirectUri = params.get("redirect_uri");
    const codeChallenge = params.get("code_challenge");
    const mcpBase = params.get("mcp_base");
    const stateParam = params.get("state");
    if (
      clientId === null ||
      redirectUri === null ||
      codeChallenge === null ||
      !isTrustedMcpBase(mcpBase)
    ) {
      return null;
    }
    return { clientId, redirectUri, codeChallenge, mcpBase, stateParam };
  }, [params]);

  if (session === null) {
    // Shouldn't happen (route is auth-gated) — fail safe to login.
    return <Navigate to="/login" replace />;
  }

  if (req === null) {
    return (
      <div className="mcpc">
        <div className="mcpc__card" role="alert">
          <div className="mcpc__eyebrow">Connection request</div>
          <h1 className="mcpc__title">This request is invalid</h1>
          <p className="mcpc__lede">
            The connection link is missing required information or points to an
            unrecognized server. Start again from the “Add custom connector”
            screen in Claude.
          </p>
        </div>
      </div>
    );
  }

  async function handleAllow(): Promise<void> {
    if (req === null) return;
    setError(null);
    setSubmitting(true);
    const jwt = await getFreshJwt();
    if (jwt === null) {
      setError("Your session expired. Reload the page and try again.");
      setSubmitting(false);
      return;
    }
    // Build + submit a hidden form: a top-level POST so the server's 302 carries
    // the browser back to claude.ai.
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${req.mcpBase}/authorize/complete`;
    const fields: Record<string, string> = {
      client_id: req.clientId,
      redirect_uri: req.redirectUri,
      code_challenge: req.codeChallenge,
      id_token: jwt,
    };
    if (req.stateParam !== null) fields["state"] = req.stateParam;
    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  }

  if (declined) {
    return (
      <div className="mcpc">
        <div className="mcpc__card" role="status">
          <div className="mcpc__eyebrow">Connection request</div>
          <h1 className="mcpc__title">You declined this connection</h1>
          <p className="mcpc__lede">
            Claude was not granted access. It’s safe to close this tab.
          </p>
        </div>
      </div>
    );
  }

  const tenantLabel = session.studioId ?? "the platform workspace";

  return (
    <div className="mcpc">
      <div className="mcpc__card">
        <div className="mcpc__eyebrow">Connect to ContractStudio</div>
        <h1 className="mcpc__title">Allow Claude to access your workspace?</h1>
        <p className="mcpc__lede">
          Claude is requesting permission to use ContractStudio’s MCP tools on
          your behalf. This connection is scoped to your account and your
          workspace — it can’t reach other tenants’ data.
        </p>

        <dl className="mcpc__facts">
          <div className="mcpc__fact">
            <dt>Signed in as</dt>
            <dd>{session.email}</dd>
          </div>
          <div className="mcpc__fact">
            <dt>Workspace</dt>
            <dd>{tenantLabel}</dd>
          </div>
          <div className="mcpc__fact">
            <dt>Access</dt>
            <dd>Run MCP tools (search precedents, review contracts, drafting)</dd>
          </div>
        </dl>

        {error !== null && (
          <p className="mcpc__error" role="alert">
            {error}
          </p>
        )}

        <div className="mcpc__actions">
          <Button
            variant="secondary"
            onClick={() => setDeclined(true)}
            disabled={submitting}
          >
            Deny
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            onClick={() => void handleAllow()}
          >
            Allow access
          </Button>
        </div>
      </div>
    </div>
  );
}

// [S-475] Unauthenticated entry to /mcp-consent. Stash the full intended URL
// (with OAuth params) so Login can return here after sign-in, then redirect to
// login. Rendered only in App.tsx's unauthenticated route set.
export function McpConsentLoginRedirect(): JSX.Element {
  const location = useLocation();
  stashOAuthReturn(location.pathname + location.search);
  return <Navigate to="/login" replace />;
}
